import { useEffect, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import type { TaskType, TaskField, TaskFieldKind } from "../lib/types";
import * as work from "../lib/workClient";

/* Task Type designer. Define reusable task templates whose required fields
 * gate completion — a "definition of done" for a kind of work. Admins add
 * fields (text, number, select, date, checkbox, account) and mark which are
 * required; a task of that type can't be closed until they're all filled. */

const KINDS: { kind: TaskFieldKind; label: string }[] = [
  { kind: "text", label: "Text" }, { kind: "number", label: "Number" }, { kind: "select", label: "Select" },
  { kind: "date", label: "Date" }, { kind: "checkbox", label: "Checkbox" }, { kind: "account", label: "Account" },
];
const blankField = (): TaskField => ({ key: "", label: "", kind: "text", required: true });

export default function TaskTypes() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [types, setTypes] = useState<TaskType[]>([]);
  const [editing, setEditing] = useState<TaskType | "new" | null>(null);

  const refresh = () => work.loadTaskTypes(ws).then((r) => { setMode(r.mode); setTypes(r.types); });
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ws]);

  const remove = async (t: TaskType) => { if (t.system) return; await work.deleteTaskType(mode, ws, t.id); refresh(); };
  const onSaved = () => { setEditing(null); refresh(); };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Configure</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Task types</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Templates that define the "definition of done." Add required fields and a task of that type can't be completed until they're filled — no more half-finished escalations or QBRs closed without a next step.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className={`mode-pill ${mode === "api" ? "live" : ""}`}><i />{mode === "api" ? "Live API" : "Local demo"}</span>
          <button className="btn btn--primary btn--sm" onClick={() => setEditing("new")}><Icon name="plus" size={14} /> New type</button>
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {types.length === 0 ? <div className="card"><div className="card__bd"><Empty>No task types yet.</Empty></div></div> :
          types.map((t) => {
            const required = t.fields.filter((f) => f.required);
            return (
              <div key={t.id} className="card">
                <div className="card__bd">
                  <div className="row between" style={{ alignItems: "flex-start" }}>
                    <div className="row" style={{ gap: 12, alignItems: "center" }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--corona-soft)", color: "var(--corona-ink)", display: "grid", placeItems: "center" }}><Icon name={t.icon || "check"} size={18} /></div>
                      <div>
                        <div className="row" style={{ gap: 8, alignItems: "center" }}>
                          <b style={{ fontSize: 15 }}>{t.name}</b>
                          {t.system && <span className="tag">Built-in</span>}
                        </div>
                        {t.description && <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>{t.description}</div>}
                      </div>
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      <button className="btn btn--ghost btn--sm" onClick={() => setEditing(t)}>Edit</button>
                      {!t.system && <button className="btn btn--ghost btn--sm" onClick={() => remove(t)}>Delete</button>}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 7, flexWrap: "wrap", marginTop: 14 }}>
                    {t.fields.length === 0 ? <span className="faint" style={{ fontSize: 12.5 }}>No fields</span> :
                      t.fields.map((f) => (
                        <span key={f.key} className="tag" style={f.required ? { borderColor: "rgba(187,71,44,.3)", color: "var(--risk)" } : undefined} title={`${f.kind}${f.required ? " · required" : " · optional"}`}>
                          {f.required && <Icon name="lock" size={10} />} {f.label}
                        </span>
                      ))}
                  </div>
                  <div className="faint" style={{ fontSize: 11.5, marginTop: 10 }}>{required.length} required field{required.length === 1 ? "" : "s"} gate completion</div>
                </div>
              </div>
            );
          })}
      </div>

      {editing && <TypeEditor ws={ws} mode={mode} initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}

function TypeEditor({ ws, mode, initial, onClose, onSaved }: {
  ws: ReturnType<typeof useWorkspace>["ws"]; mode: work.Mode; initial: TaskType | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [fields, setFields] = useState<TaskField[]>(initial?.fields.map((f) => ({ ...f })) ?? [blankField()]);
  const [busy, setBusy] = useState(false);
  const valid = name.trim() && fields.every((f) => f.label.trim());

  const setField = (i: number, patch: Partial<TaskField>) => setFields((s) => s.map((f, j) => j === i ? { ...f, ...patch } : f));
  const addField = () => setFields((s) => [...s, blankField()]);
  const removeField = (i: number) => setFields((s) => s.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true);
    const clean = fields.filter((f) => f.label.trim()).map((f) => ({
      key: f.key || f.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
      label: f.label.trim(), kind: f.kind, required: f.required,
      ...(f.kind === "select" ? { options: (f.options ?? []).filter(Boolean) } : {}),
      ...(f.help ? { help: f.help } : {}),
    }));
    if (initial) await work.updateTaskType(mode, ws, initial.id, { name: name.trim(), description: description.trim() || undefined, fields: clean });
    else await work.createTaskType(mode, ws, { name: name.trim(), description: description.trim() || undefined, fields: clean });
    setBusy(false); onSaved();
  };

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}><Icon name="check" size={16} /><b>{initial ? `Edit ${initial.name}` : "New task type"}</b></div>
          <button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button>
        </div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14, maxHeight: "66vh", overflowY: "auto" }}>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Type name</span>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Escalation" autoFocus />
          </label>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Description</span>
            <input className="inp" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this kind of task is for" />
          </label>

          <div className="labeled__l" style={{ marginTop: 4 }}>Completion fields</div>
          <div style={{ display: "grid", gap: 10 }}>
            {fields.map((f, i) => (
              <div key={i} className="card" style={{ boxShadow: "none" }}>
                <div className="card__bd" style={{ display: "grid", gap: 8 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <input className="inp" style={{ flex: 1 }} placeholder="Field label" value={f.label} onChange={(e) => setField(i, { label: e.target.value })} />
                    <select className="inp" style={{ width: 130 }} value={f.kind} onChange={(e) => setField(i, { kind: e.target.value as TaskFieldKind })}>
                      {KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                    </select>
                    <button className="icon-btn" title="Remove field" onClick={() => removeField(i)}><Icon name="dot" size={15} /></button>
                  </div>
                  {f.kind === "select" && (
                    <input className="inp" placeholder="Options, comma-separated (e.g. On track, At risk)" value={(f.options ?? []).join(", ")}
                      onChange={(e) => setField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                  )}
                  <label className="row" style={{ gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={f.required} onChange={(e) => setField(i, { required: e.target.checked })} />
                    <span>Required to complete the task</span>
                  </label>
                </div>
              </div>
            ))}
            <button className="btn btn--ghost btn--sm" onClick={addField} style={{ justifySelf: "start" }}><Icon name="plus" size={13} /> Add field</button>
          </div>
        </div>
        <div className="compose-modal__hd" style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "none", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!valid || busy} onClick={save}>{busy ? "Saving…" : initial ? "Save changes" : "Create type"}</button>
        </div>
      </div>
    </div>
  );
}
